import type { League } from "@/types/match";
import { colorsFromString } from "@/utils/color";

const l = (
  id: string,
  name: string,
  shortName: string,
  country: string,
  category: League["category"]
): League => ({
  id,
  name,
  shortName,
  country,
  category,
  emblemUrl: null,
  accentColor: colorsFromString(id).primary,
});

export const allLeagues: League[] = [
  // ── Domestic leagues ──────────────────────────────────────────────
  l("epl", "Premier League", "EPL", "England", "Domestic League"),
  l("laliga", "La Liga", "La Liga", "Spain", "Domestic League"),
  l("seriea", "Serie A", "Serie A", "Italy", "Domestic League"),
  l("bundesliga", "Bundesliga", "Bundesliga", "Germany", "Domestic League"),
  l("ligue1", "Ligue 1", "Ligue 1", "France", "Domestic League"),
  l("eredivisie", "Eredivisie", "Eredivisie", "Netherlands", "Domestic League"),
  l("primeiraliga", "Primeira Liga", "Liga Portugal", "Portugal", "Domestic League"),
  l("scottishprem", "Scottish Premiership", "SPFL", "Scotland", "Domestic League"),
  l("saudiproleague", "Saudi Pro League", "SPL", "Saudi Arabia", "Domestic League"),
  l("mls", "Major League Soccer", "MLS", "USA / Canada", "Domestic League"),
  l("brasileirao", "Brasileirão Série A", "Brasileirão", "Brazil", "Domestic League"),
  l("argentinaliga", "Liga Profesional de Fútbol", "Liga Argentina", "Argentina", "Domestic League"),
  l("superlig", "Süper Lig", "Süper Lig", "Turkey", "Domestic League"),
  l("belgianpro", "Belgian Pro League", "Jupiler Pro League", "Belgium", "Domestic League"),
  l("swisssuper", "Swiss Super League", "Super League", "Switzerland", "Domestic League"),
  l("austrianbundesliga", "Austrian Bundesliga", "Bundesliga AT", "Austria", "Domestic League"),
  l("egyptianprem", "Egyptian Premier League", "EPL Egypt", "Egypt", "Domestic League"),
  l("j1league", "J1 League", "J1 League", "Japan", "Domestic League"),
  l("chinesesuperleague", "Chinese Super League", "CSL", "China", "Domestic League"),
  l("aleague", "A-League Men", "A-League", "Australia", "Domestic League"),

  // ── Domestic cups ─────────────────────────────────────────────────
  l("facup", "FA Cup", "FA Cup", "England", "Domestic Cup"),
  l("eflcup", "EFL Cup", "Carabao Cup", "England", "Domestic Cup"),
  l("copadelrey", "Copa del Rey", "Copa del Rey", "Spain", "Domestic Cup"),
  l("coppaitalia", "Coppa Italia", "Coppa Italia", "Italy", "Domestic Cup"),
  l("dfbpokal", "DFB-Pokal", "DFB-Pokal", "Germany", "Domestic Cup"),
  l("coupedefrance", "Coupe de France", "Coupe de France", "France", "Domestic Cup"),
  l("knvbcup", "KNVB Cup", "KNVB Beker", "Netherlands", "Domestic Cup"),
  l("tacadeportugal", "Taça de Portugal", "Taça de Portugal", "Portugal", "Domestic Cup"),
  l("scottishcup", "Scottish Cup", "Scottish Cup", "Scotland", "Domestic Cup"),
  l("usopencup", "U.S. Open Cup", "US Open Cup", "USA", "Domestic Cup"),
  l("copadobrasil", "Copa do Brasil", "Copa do Brasil", "Brazil", "Domestic Cup"),

  // ── Super cups ─────────────────────────────────────────────────────
  l("communityshield", "Community Shield", "Community Shield", "England", "Super Cup"),
  l("spanishsupercup", "Spanish Super Cup", "Supercopa de España", "Spain", "Super Cup"),
  l("supercoppaitaliana", "Supercoppa Italiana", "Supercoppa", "Italy", "Super Cup"),
  l("dflsupercup", "DFL-Supercup", "DFL-Supercup", "Germany", "Super Cup"),
  l("tropheedeschampions", "Trophée des Champions", "Trophée des Champions", "France", "Super Cup"),

  // ── Continental club competitions ──────────────────────────────────
  l("ucl", "UEFA Champions League", "UCL", "Europe", "Continental Club"),
  l("uel", "UEFA Europa League", "UEL", "Europe", "Continental Club"),
  l("uecl", "UEFA Europa Conference League", "UECL", "Europe", "Continental Club"),
  l("libertadores", "CONMEBOL Copa Libertadores", "Libertadores", "South America", "Continental Club"),
  l("sudamericana", "CONMEBOL Copa Sudamericana", "Sudamericana", "South America", "Continental Club"),
  l("cafchampionsleague", "CAF Champions League", "CAF CL", "Africa", "Continental Club"),
  l("afcchampionsleague", "AFC Champions League Elite", "ACL Elite", "Asia", "Continental Club"),
  l("concacafchampionscup", "CONCACAF Champions Cup", "Champions Cup", "North America", "Continental Club"),
  l("clubworldcup", "FIFA Club World Cup", "Club World Cup", "World", "Continental Club"),

  // ── International ──────────────────────────────────────────────────
  l("worldcup", "FIFA World Cup", "World Cup", "World", "International"),
  l("euros", "UEFA European Championship", "Euros", "Europe", "International"),
  l("copaamerica", "Copa América", "Copa América", "South America", "International"),
  l("afcon", "Africa Cup of Nations", "AFCON", "Africa", "International"),
  l("nationsleague", "UEFA Nations League", "Nations League", "Europe", "International"),
  l("goldcup", "CONCACAF Gold Cup", "Gold Cup", "North America", "International"),
  l("asiancup", "AFC Asian Cup", "Asian Cup", "Asia", "International"),
  l("wcqualifiers", "FIFA World Cup Qualifiers", "WC Qualifiers", "World", "International"),
  l("friendlies", "International Friendlies", "Friendlies", "World", "International"),
];

export const leaguesById = new Map(allLeagues.map((lg) => [lg.id, lg]));
export const leagueCategories: League["category"][] = [
  "Domestic League",
  "Domestic Cup",
  "Super Cup",
  "Continental Club",
  "International",
];
